import * as React from 'react';
import { Button } from "../Button";
import Box from "@mui/material/Box";
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { IProps } from "./IProps";
import { getAxios } from '../../utils/endpoints';
import { react } from '@babel/types';
import { LeadInfo } from '../../types/LeadInfo';
import { isHTML } from '../../utils/is-html';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: React.ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

type InfoProps = {
    displayColor?: boolean;
    title: string;
    text: string;
}

const SectionTitle: React.FunctionComponent<{ title: string }> = (props): JSX.Element => {
    return (
        <Box sx={{ p: 2, backgroundColor: "#F5F5F5", borderRadius: 4, mt: 2, mb: 2 }}>
            <Typography fontSize={18} fontWeight="600" color="#1BA0E9">
                {props.title}
            </Typography>
        </Box>
    )
}

const Info: React.FunctionComponent<InfoProps> = (props): JSX.Element => {
    return (
        <Box width="100%" p={1} sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.4)", py: 1.5 }}>
            <Typography sx={{ color: "#1BA0E9" }} fontSize={20} fontWeight="bold">{props.title}</Typography>
            {isHTML(props.text) ? <div dangerouslySetInnerHTML={{ __html: props.text }} /> : <Typography mt={2}>
                {props.text ? props.text : ""}
            </Typography>}
        </Box>
    )
}

export const LeadInfoDialog: React.FunctionComponent<IProps> = (props): JSX.Element => {
    const [leadInfo, setLeadInfo] = React.useState<LeadInfo | null>(null)

    const onFetchDealerInfo = async (dealerId: string): Promise<void> => {
        const response = await getAxios(`/dealers/${dealerId}/info`, "GET");

        const data: LeadInfo = await response.data;

        setLeadInfo(data);
    }

    React.useEffect(() => {
        if (props.open) {
            onFetchDealerInfo(props.id);
        }
    }, [props.open])


    if (!leadInfo) {
        return <></>;
    }

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={props.open}
            onClose={props.onClose}
        >
            <DialogTitle>
                <SectionTitle title={leadInfo.companyName} />
            </DialogTitle>
            <DialogContent>
                <Info displayColor title="Business Name" text={leadInfo.businessName} />
                <Info title={leadInfo.transferLabel} text={leadInfo.dealerMsg} />
                <Info displayColor title="Dealer Time Zone" text={leadInfo.dealerTimeZone} />
                <Info title="Call Center Hours" text={leadInfo.callCenterHours} />
                <Info displayColor title="Promotion" text={leadInfo.promotion} />
                <Info title='Demo Info' text={leadInfo.demoInfo1} />
                <Info displayColor title='Warranty Info' text="" />
                <Info title='Installer Info' text={leadInfo.installerInfo} />
                <Info displayColor title='Showroom Address' text={leadInfo.showroomAddress} />
                <Info title='Dealer Phone' text={leadInfo.dealerPhone} />
                <Info displayColor title="Website Comments" text={leadInfo.website} />
            </DialogContent>
            <DialogActions sx={{ px: 2, my: 2 }}>
                <Button height={35} label="Close" onClick={props.onClose} />
            </DialogActions>
        </Dialog>
    );
}